<template>
  <section
    :class="[
      'list-navigator',
      isActive ? 'list-navigator--active' : 'list-navigator--inactive',
      $attrs.class,
    ]"
    @click="handleOnActivate"
  >
    <span class="title-medium-regular">{{ title }}</span>
    <div class="buttons-group">
      <button v-focus v-if="!isActive">
        <Icon name="faChevronRight" />
      </button>
      <button
        v-focus
        v-if="isActive"
        :aria-label="labelPrev"
        @click.stop="previous"
      >
        <Icon color="white" name="faChevronUp" />
      </button>
      <button
        v-focus
        v-if="isActive"
        :aria-label="labelNext"
        @click.stop="next"
      >
        <Icon color="white" name="faChevronDown" />
      </button>
      <button v-focus v-if="isActive" @click.stop="handleOnClose">
        <Icon color="white" name="faXmark" />
      </button>
    </div>
  </section>
</template>
<script setup lang="ts" generic="T">
import { ref } from "vue";
import Icon from "../atoms/Icon/Icon.vue";

type GenericListNavigatorProps<T> = {
  labelNext?: string;
  labelPrev?: string;
  list: T[];
  title?: string;
};
const { labelNext, labelPrev, list, title } =
  defineProps<GenericListNavigatorProps<T>>();
const emit = defineEmits<{ close: [void]; change: [T]; activate: [void] }>();

const cursor = ref<number | null>(0);
const isActive = ref(false);

const next = () => {
  if (cursor.value === null || !list.length) return;
  cursor.value = (cursor.value + 1) % list.length;
  notifyCursorChanged();
};

const previous = () => {
  if (cursor.value === null || !list.length) return;
  cursor.value = (cursor.value + list.length - 1) % list.length;
  notifyCursorChanged();
};

const notifyCursorChanged = () => {
  if (cursor.value !== null) {
    emit("change", list[cursor.value]);
  }
};

const handleOnClose = () => {
  if (!isActive.value) {
    return;
  }
  isActive.value = false;
  cursor.value = null;
  emit("close");
};

const handleOnActivate = () => {
  if (isActive.value) {
    return;
  }
  isActive.value = true;
  if (cursor.value === null) {
    cursor.value = 0;
  }
  emit("activate");
};
</script>
<style scoped lang="scss">
.list-navigator {
  border-radius: 8px;
  display: inline-flex;
  padding: 8px 10px;
  align-items: center;
  gap: 10px;
  margin: auto;
}
.buttons-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.list-navigator--active {
  background-color: var(--clr-p7);
  color: white;
}

.list-navigator--inactive {
  background-color: transparent;
  color: var(--clr-n8);
  border: 1px solid var(--clr-p2);
}
</style>
